import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, date, download, money } from '../lib/api';
import { messageFor } from '../lib/session';
import { Chip, Empty, Loading, Notice } from '../components/ui';

type ProjectRow = {
  id: string; name: string; start_date: string;
  agreement_count: number; open_invoice_count: number; outstanding_cents: number;
};

type Agreement = {
  id: string; title: string; version: number; status: string;
  sent_at: string | null; signed_at: string | null; expires_at: string | null; signing_url: string | null;
};

type Invoice = {
  id: string; number: string; description: string; amount_cents: number; currency: string;
  status: string; issue_date: string; due_date: string; payment_url: string | null; paid_at: string | null;
};

export function CustomerProjects({ customerName }: { customerName: string }) {
  const [projects, setProjects] = useState<ProjectRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void api.get<{ projects: ProjectRow[] }>('/api/portal/projects')
      .then((result) => setProjects(result.projects))
      .catch((cause) => setError(messageFor(cause)));
  }, []);

  if (error) return <Notice kind="error">{error}</Notice>;
  if (!projects) return <Loading />;
  return (
    <>
      <header className="simple-page-head"><div><span>Customer portal</span><h1>{customerName}</h1><p>Select a project to view its agreements and payments.</p></div></header>
      {projects.length === 0 ? <div className="simple-panel"><Empty title="No projects yet">WebForGrowth will add your project here.</Empty></div> : (
        <div className="project-grid">
          {projects.map((project) => (
            <Link className="project-card" to={`/portal/projects/${project.id}`} key={project.id}>
              <span className="project-kicker">Started {date(project.start_date)}</span>
              <h2>{project.name}</h2>
              <dl>
                <div><dt>Agreements</dt><dd>{project.agreement_count}</dd></div>
                <div><dt>Pending invoices</dt><dd>{project.open_invoice_count}</dd></div>
                <div><dt>Amount due</dt><dd>{money(project.outstanding_cents)}</dd></div>
              </dl>
              <strong>Open project <span aria-hidden="true">&rarr;</span></strong>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

export function CustomerProject() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<{ project: { id: string; name: string; start_date: string }; agreements: Agreement[]; invoices: Invoice[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void api.get<NonNullable<typeof data>>(`/api/portal/projects/${id}`)
      .then(setData).catch((cause) => setError(messageFor(cause)));
  }, [id]);

  async function getAgreement(agreementId: string) {
    try { await download(`/api/portal/projects/${id}/agreements/${agreementId}/download`); }
    catch (cause) { setError(messageFor(cause)); }
  }

  if (error && !data) return <Notice kind="error">{error}</Notice>;
  if (!data) return <Loading />;
  const pending = data.invoices.filter((invoice) => !['paid', 'void'].includes(invoice.status));
  const due = pending.reduce((sum, invoice) => sum + invoice.amount_cents, 0);

  return (
    <>
      <Link className="back-link" to="/portal">&larr; All projects</Link>
      <header className="simple-page-head project-head">
        <div><span>Project</span><h1>{data.project.name}</h1><p>Started {date(data.project.start_date)}</p></div>
        <div className="amount-due"><span>Amount due</span><strong>{money(due, pending[0]?.currency)}</strong></div>
      </header>
      {error && <Notice kind="error">{error}</Notice>}

      <section className="simple-panel section-panel">
        <div className="simple-panel-head"><div><span>Documents</span><h2>Agreements</h2></div><b>{data.agreements.length}</b></div>
        {data.agreements.length === 0 ? <Empty title="No agreements yet" /> : (
          <div className="simple-list">
            {data.agreements.map((agreement) => (
              <article className="simple-list-row" key={agreement.id}>
                <div className="row-main"><strong>{agreement.title}</strong><small>Version {agreement.version} · Sent {date(agreement.sent_at)}</small></div>
                <Chip value={agreement.status} />
                <div className="row-actions">
                  {agreement.signing_url && <a className="btn small" href={agreement.signing_url}>Review &amp; sign</a>}
                  {['signed', 'countersigned'].includes(agreement.status) && <button className="secondary small" onClick={() => void getAgreement(agreement.id)}>Download</button>}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="simple-panel section-panel">
        <div className="simple-panel-head"><div><span>Billing</span><h2>Payments &amp; invoices</h2></div><b>{data.invoices.length}</b></div>
        {data.invoices.length === 0 ? <Empty title="No invoices yet" /> : (
          <div className="simple-list">
            {data.invoices.map((invoice) => (
              <article className="simple-list-row invoice" key={invoice.id}>
                <div className="row-main"><strong>{invoice.number}</strong><small>{invoice.description} · Due {date(invoice.due_date)}</small></div>
                <div className="row-total"><strong>{money(invoice.amount_cents, invoice.currency)}</strong><Chip value={invoice.status} /></div>
                <div className="row-actions">
                  <a className="btn secondary small" href={`/api/portal/projects/${id}/invoices/${invoice.id}/pdf`}>Invoice</a>
                  {invoice.status === 'paid' && <a className="btn secondary small" href={`/api/portal/projects/${id}/invoices/${invoice.id}/pdf?type=receipt`}>Receipt</a>}
                  {!['paid', 'void'].includes(invoice.status) && invoice.payment_url && <a className="btn small" href={invoice.payment_url}>Pay now</a>}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
